import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight, LuLock, LuUsers } from 'react-icons/lu';
import Navbar from '../Items/Navbar';
import PageMeta from '../seo/PageMeta';
import AuthContext from '../shared/AuthContext';
import {
  FEATURE,
  FEATURE_LABELS,
  normalizePlan,
  requiredPlanLabel,
} from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import PlanTierLabel from './PlanTierLabel';
import './PlanUpgradeNotice.css';

function planDisplayName(plan) {
  const p = normalizePlan(plan);
  if (p === 'FREE') return 'Free';
  if (p === 'PRO') return 'Pro';
  return 'Team';
}

function upgradeHints(feature, requiredTier) {
  if (feature === FEATURE.TEAM_LEADERBOARD_ANALYTICS) {
    return [
      'Organization-wide XP rankings and learning scores',
      'Manager dashboard with team engagement KPIs',
    ];
  }
  if (feature === FEATURE.LEADERBOARD_RANK) {
    return [
      'Weekly, monthly, and all-time XP rankings',
      'See your rank and compete with other learners',
    ];
  }
  if (feature === FEATURE.ANALYTICS_ADVANCED || feature === FEATURE.ANALYTICS_EXPORT) {
    return [
      '14-day activity charts and topic breakdown',
      'Full review schedule snapshot and JSON export in Settings',
    ];
  }
  if (requiredTier === 'Team') {
    return [
      'Create and manage a shared organization',
      'Collaborate with teammates and handle invites',
    ];
  }
  const label = FEATURE_LABELS[feature] || 'This capability';
  return [`Includes ${label.toLowerCase()} and other Pro tools`];
}

export default function PlanUpgradeNotice({ feature, embedded = false }) {
  const { user } = useContext(AuthContext);
  const userProfile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');
  const { plan } = useUserPlan();
  const required = requiredPlanLabel(feature);
  const requiredKey = required.toLowerCase();
  const currentName = planDisplayName(plan);
  const featureLabel = FEATURE_LABELS[feature] || 'This feature';
  const hints = upgradeHints(feature, required);
  const titleId = embedded ? 'plan-upgrade-title-embedded' : 'plan-upgrade-title';

  const card = (
        <article
          className={`plan-upgrade-card plan-upgrade-card--${requiredKey}`}
          aria-labelledby={titleId}
        >
          <div className="plan-upgrade-card-top">
            <div className="plan-upgrade-icon" aria-hidden>
              {required === 'Team' ? <LuUsers size={26} strokeWidth={2} /> : <LuLock size={26} strokeWidth={2} />}
            </div>
            <div className="plan-upgrade-plan-flow">
              <span className="plan-upgrade-plan-pill plan-upgrade-plan-pill--current">
                {currentName} plan
              </span>
              <LuArrowRight className="plan-upgrade-flow-arrow" aria-hidden />
              <PlanTierLabel tier={required} className="plan-upgrade-tier-badge" />
            </div>
          </div>

          <h1 id={titleId} className="plan-upgrade-title">
            Upgrade to unlock {featureLabel.toLowerCase()}
          </h1>
          <p className="plan-upgrade-lead">
            {featureLabel} is included on the {required} plan. Compare options in billing or finish checkout to
            start using it right away.
          </p>

          <ul className="plan-upgrade-hints">
            {hints.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <div className="plan-upgrade-actions">
            <Link to="/settings?tab=billing" className="plan-upgrade-btn plan-upgrade-btn--primary">
              View plans &amp; billing
            </Link>
            <Link to="/dashboard" className="plan-upgrade-btn plan-upgrade-btn--secondary">
              Back to Study hub
            </Link>
          </div>
        </article>
  );

  if (embedded) {
    return <div className="plan-upgrade-embedded">{card}</div>;
  }

  return (
    <div className="plan-upgrade-page">
      <PageMeta title="Upgrade required" description="This feature is not on your current plan." />
      <Navbar userProfile={userProfile} loading={false} />
      <main className="plan-upgrade-shell">{card}</main>
    </div>
  );
}
